/**
 * GanttChartPage - Gantt chart view for job scheduling
 * Refactored to use extracted hooks and components
 * Epic 46: Added interval validation modal
 */
import React, { useState } from 'react';
import { MainLayout } from '../components/MainLayout';

// Hooks
import { useGanttDatePicker } from '../hooks/useGanttDatePicker';
import { useGanttJobs } from '../hooks/useGanttJobs';
import { useGanttDragDrop } from '../hooks/useGanttDragDrop';

// Components
import {
  GanttHeader,
  GanttFilters,
  GanttStats,
  GanttTimeline,
  GanttLegend,
} from '../components/gantt';
import IntervaloAgendamentoModal from '../components/IntervaloAgendamentoModal';

export const GanttChartPage: React.FC = () => {
  // UI State
  const [showFilters, setShowFilters] = useState(false);
  const [allFiltersExpanded, setAllFiltersExpanded] = useState(false);
  const [isBusinessHours, setIsBusinessHours] = useState(true);

  // Filter State
  const [selectedHospitals, setSelectedHospitals] = useState<(string | number)[]>([]);
  const [selectedClientes, setSelectedClientes] = useState<(string | number)[]>([]);
  const [selectedAuditores, setSelectedAuditores] = useState<(string | number)[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<(string | number)[]>([]);
  const [selectedCodHospitalCliente, setSelectedCodHospitalCliente] = useState<(string | number)[]>([]);

  // Time Range Constants
  const startHour = isBusinessHours ? 6 : 0;
  const endHour = isBusinessHours ? 20 : 24;
  const totalHours = endHour - startHour;

  // Date Picker Hook
  const {
    selectedDate,
    showDatePicker,
    setShowDatePicker,
    pickerMonth,
    datePickerRef,
    handleDateSelect,
    handlePrevMonth,
    handleNextMonth,
    handleToday,
    handlePreviousDay,
    handleNextDay,
    dateParam,
  } = useGanttDatePicker();

  // Jobs Hook
  const {
    jobs,
    setJobs,
    loading,
    fetchJobs,
    filteredJobs,
    stats,
    hospitalOptions,
    clienteOptions,
    auditorOptions,
    codHospitalClienteOptions,
  } = useGanttJobs({
    selectedDate,
    dateParam,
    selectedHospitals,
    selectedClientes,
    selectedAuditores,
    selectedStatus,
    selectedCodHospitalCliente,
  });

  // Drag and Drop Hook
  const {
    activeId,
    draggedJob,
    dragPreviewPosition,
    resizingJob,
    timelineRef,
    timelinePixelWidth,
    sensors,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleResizeStart,
    // Epic 46: Interval validation
    // Epic 47: Include handleIntervaloConfirmWithJustificativas
    showIntervaloModal,
    intervaloValidacoes,
    intervaloDataSelecionada,
    intervaloLoading,
    handleIntervaloConfirm,
    handleIntervaloConfirmWithJustificativas,
    handleIntervaloCancel,
  } = useGanttDragDrop({
    jobs,
    setJobs,
    filteredJobs,
    fetchJobs,
    startHour,
    totalHours,
    loading,
    isBusinessHours,
    selectedDate, // Epic 46: Required for interval validation
  });

  // Computed Values
  const activeFiltersCount = selectedHospitals.length + selectedClientes.length + selectedAuditores.length + selectedStatus.length + selectedCodHospitalCliente.length;

  const clearFilters = () => {
    setSelectedHospitals([]);
    setSelectedClientes([]);
    setSelectedAuditores([]);
    setSelectedStatus([]);
    setSelectedCodHospitalCliente([]);
  };

  return (
    <MainLayout>
      <div className="p-6">
        {/* Header with Date Navigation */}
        <GanttHeader
          selectedDate={selectedDate}
          showDatePicker={showDatePicker}
          setShowDatePicker={setShowDatePicker}
          pickerMonth={pickerMonth}
          datePickerRef={datePickerRef}
          onDateSelect={handleDateSelect}
          onPrevMonth={handlePrevMonth}
          onNextMonth={handleNextMonth}
          onToday={handleToday}
          onPreviousDay={handlePreviousDay}
          onNextDay={handleNextDay}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          activeFiltersCount={activeFiltersCount}
        />

        {/* Filters Panel */}
        {showFilters && (
          <GanttFilters
            hospitalOptions={hospitalOptions}
            clienteOptions={clienteOptions}
            auditorOptions={auditorOptions}
            codHospitalClienteOptions={codHospitalClienteOptions}
            selectedHospitals={selectedHospitals}
            selectedClientes={selectedClientes}
            selectedAuditores={selectedAuditores}
            selectedStatus={selectedStatus}
            selectedCodHospitalCliente={selectedCodHospitalCliente}
            setSelectedHospitals={setSelectedHospitals}
            setSelectedClientes={setSelectedClientes}
            setSelectedAuditores={setSelectedAuditores}
            setSelectedStatus={setSelectedStatus}
            setSelectedCodHospitalCliente={setSelectedCodHospitalCliente}
            allFiltersExpanded={allFiltersExpanded}
            setAllFiltersExpanded={setAllFiltersExpanded}
            onClearFilters={clearFilters}
          />
        )}

        {/* Stats Cards */}
        <GanttStats stats={stats} />

        {/* Gantt Timeline */}
        <GanttTimeline
          loading={loading}
          filteredJobs={filteredJobs}
          jobs={jobs}
          startHour={startHour}
          totalHours={totalHours}
          timelineRef={timelineRef}
          timelinePixelWidth={timelinePixelWidth}
          sensors={sensors}
          activeId={activeId}
          dragPreviewPosition={dragPreviewPosition}
          resizingJob={resizingJob}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
          onResizeStart={handleResizeStart}
        />

        {/* Legend */}
        <GanttLegend
          isBusinessHours={isBusinessHours}
          setIsBusinessHours={setIsBusinessHours}
        />
      </div>

      {/* Epic 46: Interval Validation Modal */}
      {/* Epic 47: Using onConfirmWithJustificativas to capture justifications */}
      <IntervaloAgendamentoModal
        isOpen={showIntervaloModal}
        onClose={handleIntervaloCancel}
        onConfirm={handleIntervaloConfirm}
        onConfirmWithJustificativas={handleIntervaloConfirmWithJustificativas}
        validacoes={intervaloValidacoes}
        dataSelecionada={intervaloDataSelecionada}
        loading={intervaloLoading}
      />
    </MainLayout>
  );
};

export default GanttChartPage;
